import { ColumnDef } from "@tanstack/react-table";
import { BiDotsHorizontalRounded } from "react-icons/bi";

// Helper function
const formatDate = (params: any) => {
	if (!params.value) return "";
	const date = new Date(params.value);
	return date.toLocaleDateString();
};

export const CountryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "name",
		header: "Country",
		size: 250,
		minSize: 200,
		cell: ({ row }) => {
			return (
				<div className="w-full flex items-center gap-3">
					<img
						src={row.original.flagUrl}
						alt="flag"
						className="w-8 h-8 rounded-lg object-cover"
					/>
					<p className="text-sm line-clamp-1">{row.original.name}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "code",
		header: "Country Code",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => getValue(),
	},
	{
		accessorKey: "currency",
		header: "Currency",
		size: 220,
		minSize: 180,
		cell: ({ row }) => {
			const currency = row.original.currency;
			return currency ? `${currency.name} (${currency.symbol})` : "--";
		},
	},
	{
		accessorKey: "active",
		header: "Active",
		size: 140,
		minSize: 100,
		cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
	},
	{
		accessorKey: "taxNumberRequired",
		header: "Tax Number Required",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => {
			const value = getValue();
			return value === null || value === undefined
				? "--"
				: value
					? "Yes"
					: "No";
		},
	},
	{
		accessorKey: "minimumEmploymentAge",
		header: "Minimum Employment Age",
		size: 220,
		minSize: 180,
		cell: ({ getValue }) => {
			const value = getValue() as number | null;
			return value ?? "--";
		},
	},
	{
		accessorKey: "applyLoanDeductionOnBasic",
		header: "Apply Loan Deduction On Basic",
		size: 250,
		minSize: 200,
		cell: ({ getValue }) => {
			const value = getValue();
			return value === null || value === undefined
				? "--"
				: value
					? "Yes"
					: "No";
		},
	},
	{
		accessorKey: "maxLoanDeductionPercentage",
		header: "Max Loan Deduction Percentage",
		size: 250,
		minSize: 200,
		cell: ({ getValue }) => {
			const value = getValue() as number | null;
			return value ? `${(Number(value) * 100).toFixed(2)}%` : "--";
		},
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "modifiedAt",
		header: "Date Modified",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];

export const CountryInterestRateHistoryColumns: ColumnDef<any>[] = [
	{
		accessorKey: "timestamp",
		header: "Recorded At",
		size: 220,
		minSize: 180,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
		sortingFn: (rowA, rowB) => {
			const dateA = new Date(rowA.original.timestamp).getTime();
			const dateB = new Date(rowB.original.timestamp).getTime();
			return dateA - dateB;
		},
	},
	{
		accessorKey: "previousValue",
		header: "Previous Value",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => {
			const value = getValue() as number | null;
			return value === null ? "--" : `${(Number(value) * 100).toFixed(2)}%`;
		},
	},
	{
		accessorKey: "currentValue",
		header: "Current Value",
		size: 200,
		minSize: 150,
		cell: ({ getValue }) => `${(Number(getValue()) * 100).toFixed(2)}%`,
	},
	{
		accessorKey: "actor",
		header: "Actor",
		size: 220,
		minSize: 180,
		cell: ({ getValue }) => {
			const actor = getValue() as any;
			return actor?.name || "User";
		},
	},
];
